import axios from 'axios';
import flatten from 'lodash/flatten';
import { useEffect, useMemo, useState } from 'react';

import useMonolithApiQuery from '@/hooks/api/useMonolithApiQuery';
import { SimpleErrorResponse } from '@/models/Error';
import Pipelinables from '@/models/Pipelineables';
import { MachineGroupApiItem, MachineRegionId, StackGroupApiItem } from '@/types/StackAndMachine';
import { camelCaseKeys } from '@/utils/changeCase';
import { camelCaseMachineTypeData } from '@/utils/machineType';
import { getStartBuildDialogDataPath } from '@/utils/monolithApiRouteService';

type StartBuildDialogData = {
  hasTriggerMap: false;
  workflows: Pipelinables[];
  pipelines: Pipelinables[];
  groupedStacks: StackGroupApiItem[];
  groupedMachines: MachineGroupApiItem[];
  region: MachineRegionId;
};

const useStartBuildDialogData = ({ appSlug, params }: { appSlug: string; params?: Record<string, string> }) => {
  const [isEnabled, setEnabled] = useState(true);
  const {
    data: startBuildDialogData,
    isLoading,
    error,
  } = useMonolithApiQuery<StartBuildDialogData, SimpleErrorResponse>(
    ['app', appSlug, params, 'start-build-dialog-data'],
    getStartBuildDialogDataPath(appSlug, {
      params,
    }),
    {
      enabled: isEnabled,
      requestConfig: {
        transformResponse: flatten([
          axios.defaults.transformResponse || [],
          (data: any) => {
            return (
              data && {
                ...camelCaseKeys(data),
                groupedMachines: data.groupedMachines.map((groupData: any) => {
                  return { ...groupData, machines: groupData.machines.map(camelCaseMachineTypeData) };
                }),
              }
            );
          },
        ]),
      },
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const hasTriggerMap = useMemo(() => {
    return Boolean(startBuildDialogData?.hasTriggerMap);
  }, [startBuildDialogData?.hasTriggerMap]);

  const pipelines = useMemo(() => {
    return startBuildDialogData?.pipelines?.map((p) => p.id) ?? [];
  }, [startBuildDialogData?.pipelines]);

  const workflows = useMemo(() => {
    return startBuildDialogData?.workflows?.map((w) => w.id) ?? [];
  }, [startBuildDialogData?.workflows]);

  useEffect(() => {
    if (!params) {
      setEnabled(false);
    }
  }, [params]);

  return {
    hasTriggerMap,
    isLoading,
    error,
    pipelines,
    workflows,
    groupedStacks: startBuildDialogData?.groupedStacks ?? [],
    groupedMachines: startBuildDialogData?.groupedMachines ?? [],
    region: startBuildDialogData?.region,
  };
};

export default useStartBuildDialogData;
